import {processTabs} from "../../../common/util/util-helpers";
import Resources from "../../../data/services/resources";
import React, {useState} from "react";
import Layout from "../../../common/components/layout";
import Page from "../../../common/components/layout/layout-components/page";
import PageHeader from "../../../common/components/layout/layout-components/page/page-header";
import NavResponsive from "../../../common/components/nav-responsive";
import {Field} from "../../../data/services/fields";
import moment from "moment/moment";
import WorkOrdersReportsTab from "./work-orders-reports-tab";
import {DEFAULT_METADATA_SELECT_SEARCH_QUERY} from "../../../common/util/util-consts";
import CellButton from "../../../common/components/resource-table/table-components/cell-button";
import {checkPerm} from "../../../util/util";
import {READ_PERM} from "../../../util/util-constants";
import {useDispatch} from "react-redux";
import {showGlobalModal} from "../../../data/actions/ui";
import LocalStorage from "../../../util/localStorage";
import StockMovementReportTab from "./stock-movement-report-tab";
import ProfitMarginReportTab from "./profit-margin-report-tab";
import CostPerMileReportTab from "./cost-per-mile-report-tab"
import CostPerCodeReportTab from "./cost-per-code-report-tab";
import InventoryVelocityReportTab from "./inventory-velocity-report-tab";
import InventoryAgingReportTab from "./inventory-aging-report-tab";
// The same grid the Parts page shows under its own Inventory tab. Imported rather than
// copied, so the two stay in step -- and it stays on the Parts page either way.
import PartsReportTab from "../../parts-view/reports-tab";

const lastMonthStart = () => moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD')
const lastMonthEnd = () => moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
const WO_REPORTS_DATES_KEY = 'wo_reports_dates'
const getSavedDates = () => LocalStorage.get(WO_REPORTS_DATES_KEY) || {}

export default function WorkOrdersReports({translate, match, history, location}) {
    const dispatch = useDispatch();
    function getTabs() {
        return processTabs([
            {name: 'CostPerMile', resource: Resources.WorkOrdersReportsMaintenanceCostMileage, labelRaw: 'Maintenance Summary Cost'},
            {name: 'CostPerCode', resource: Resources.WorkOrdersReportsPerCode},
            {name: 'MaintenanceCostTruck', resource: Resources.WorkOrdersReportsMaintenanceCostTruck},
            {name: 'MaintenanceCostTrailer', resource: Resources.WorkOrdersReportsMaintenanceCostTrailer},
            // The four inventory reports read as one family and sit together, in the order
            // you would work through them: what you hold, what moved, what it earned,
            // what is not moving.
            {name: 'Inventory', resource: Resources.WorkOrderProductServicesReport},
            {name: 'StockTracking', resource: Resources.StockTracking},
            {name: 'ProfitMargin', resource: Resources.ProfitMargin},
            {name: 'InventoryVelocity', resource: Resources.InventoryExceptions},
            {name: 'InventoryAging', resource: Resources.InventoryExceptions},
        ]);
    }

    function syncDatesOnTabSwitch(nextResource) {
        const saved = getSavedDates()
        const truckKey = 'MaintenanceCostTruck_query'
        const trailerKey = 'MaintenanceCostTrailer_query'

        // Leaving a Truck/Trailer tab — pull their saved dates into wo_reports_dates
        const current = tabs.find(t => t.current)
        if (current?.resource === Resources.WorkOrdersReportsMaintenanceCostTruck) {
            const q = LocalStorage.get(truckKey) || {}
            if (q.StartDate || q.EndDate) {
                LocalStorage.set(WO_REPORTS_DATES_KEY, {StartDate: q.StartDate || saved.StartDate, EndDate: q.EndDate || saved.EndDate})
            }
        }
        if (current?.resource === Resources.WorkOrdersReportsMaintenanceCostTrailer) {
            const q = LocalStorage.get(trailerKey) || {}
            if (q.StartDate || q.EndDate) {
                LocalStorage.set(WO_REPORTS_DATES_KEY, {StartDate: q.StartDate || saved.StartDate, EndDate: q.EndDate || saved.EndDate})
            }
        }

        // Entering a Truck/Trailer tab — push wo_reports_dates into their query storage
        const latestDates = getSavedDates()
        if (nextResource === Resources.WorkOrdersReportsMaintenanceCostTruck && (latestDates.StartDate || latestDates.EndDate)) {
            LocalStorage.set(truckKey, {...(LocalStorage.get(truckKey) || {}), StartDate: latestDates.StartDate, EndDate: latestDates.EndDate})
        }
        if (nextResource === Resources.WorkOrdersReportsMaintenanceCostTrailer && (latestDates.StartDate || latestDates.EndDate)) {
            LocalStorage.set(trailerKey, {...(LocalStorage.get(trailerKey) || {}), StartDate: latestDates.StartDate, EndDate: latestDates.EndDate})
        }
    }

    // NavResponsive calls this as onTabChange(tab.resource, tab). Velocity and Aging are
    // two reports on one endpoint, so they share a resource -- and therefore a permission.
    // Matching on name is what keeps them from both going current at once.
    function handleTabChange(resource, tab) {
        syncDatesOnTabSwitch(resource)
        setTabs(
            tabs.map((it) => {
                it.current = tab ? it.name === tab.name : it.resource === resource;
                return it
            })
        )
    }

    const [tabs, setTabs] = useState(getTabs());
    const currentTab = tabs.find(tab => tab.current);

    const handleViewTruckClick = (truckID) => {
        dispatch(showGlobalModal('ViewTruckCard', truckID));
    }

    const handleViewTrailerClick = (trailerID) => {
        dispatch(showGlobalModal('ViewTrailerCard', trailerID));
    }

    return (
        <Layout
            match={match}
            history={history}
            location={location}
            translate={translate}
        >
            <Page>
                <PageHeader
                    title={translate("page.work-order.reports")}
                    titleClass="mb-5 text-2xl ml-4"
                />
                <div className="mb-5">
                    <NavResponsive
                        tabs={tabs}
                        onTabChange={handleTabChange}
                        translate={translate}
                    />
                </div>

                {currentTab.resource === Resources.WorkOrdersReportsMaintenanceCostTruck && (
                    <WorkOrdersReportsTab
                        tableKey={'TruckID'}
                        getFields={() => {
                            return {
                                'TruckID': new Field('TruckID', '', [''], false, 'select-search', {
                                    render: (item) => {
                                        if (!item) return null;

                                        if (item.TruckID) {
                                            return (
                                                <CellButton
                                                    onClick={() => checkPerm(Resources.TrucksInfo, READ_PERM) && handleViewTruckClick(item.TruckID)}
                                                >
                                                    {item.Truck}
                                                </CellButton>
                                            )
                                        } else {
                                            return (<span>{item.Truck}</span>)
                                        }
                                    },
                                    addContainerClass: "col-span-full sm:col-span-6 lg:col-span-3"
                                }),
                                'PartsAmount': new Field('PartsAmount', '', [''], false, 'money', {
                                    addContainerClass: 'col-span-full sm:col-span-6 lg:col-span-3'
                                }),
                                'LaborAmount': new Field('LaborAmount', '', [''], false, 'money', {
                                    addContainerClass: 'col-span-full sm:col-span-6 lg:col-span-3'
                                }),
                                'OtherAmount': new Field('OtherAmount', '', [''], false, 'money', {
                                    addContainerClass: 'col-span-full sm:col-span-6 lg:col-span-3'
                                }),
                                'TotalAmount': new Field('TotalAmount', '', [''], false, 'money', {
                                    addContainerClass: 'col-span-full sm:col-span-6 lg:col-span-3'
                                }),
                            }
                        }}
                        additionalQuery={{
                            StartDate: new Field('StartDate', getSavedDates().StartDate || lastMonthStart(), [], false, 'date', {}, {}),
                            EndDate: new Field('EndDate', getSavedDates().EndDate || lastMonthEnd(), [], false, 'date', {}, {}),
                            TruckIDs: new Field('TruckIDs', '', [''], false, 'select-search', {}, {
                                multi: true,
                                all: true
                            }),
                            ChargeOrganizationIDs: new Field('ChargeOrganizationIDs', '', [''], false, 'select-search', {}, {
                                multi: true,
                                all: true
                            }),
                            ChargeContactIDs: new Field('ChargeContactIDs', '', [''], false, 'select-search', {}, {
                                multi: true,
                                all: true
                            }),
                            ScheduledMaintenanceIDs: new Field('ScheduledMaintenanceIDs', '', [''], false, 'select-search', {}, {
                                multi: true,
                                all: true
                            }),
                            UpdatedByContactID: new Field('UpdatedByContactID', '', [''], false, 'select-search', {}, {menuPlacement: "top", isClearable: true}),
                            CreatedByID: new Field('CreatedByID', '', [''], false, 'select-search', {}, {menuPlacement: "top", isClearable: true}),
                        }}
                        resourcePath={currentTab.resource}
                        storagePath={currentTab.name}
                        selects={{
                            TruckIDs: {
                                api: 'api/' + Resources.TrucksQuick,
                                query: Object.assign(
                                    DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
                                    {NotSoldRetired: 1}
                                ),
                                searchMap: (item) => ({
                                    value: item.TruckID,
                                    label: item.TruckNumber
                                })
                            },
                            ChargeOrganizationIDs: {
                                api: 'api/' + Resources.OrganizationsQuick,
                                query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
                                searchMap: (item) => ({
                                    value: item.OrganizationID,
                                    label: item.LegalName
                                })
                            },
                            ChargeContactIDs: {
                                api: 'api/' + Resources.ContactsQuick,
                                query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
                                searchMap: (item) => ({
                                    value: item.ContactID,
                                    label: item.FirstName + " " + item.LastName
                                })
                            },
                            ScheduledMaintenanceIDs: {
                                api: 'api/' + Resources.WorkOrderScheduledMaintenance,
                                query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
                                searchMap: (item) => ({
                                    value: item.ScheduledMaintenanceID,
                                    label: item.ScheduledMaintenanceType + ", " + item.ScheduleNotes
                                })
                            },
                            UpdatedByContactID: {
                                api: 'api/' + Resources.ContactsQuick,
                                query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
                                searchMap: (item) => ({
                                    value: item.ContactID,
                                    label: item.FirstName + " " + item.LastName
                                })
                            },
                            CreatedByID: {
                                api: 'api/' + Resources.ContactsQuick,
                                query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
                                searchMap: (item) => ({
                                    value: item.ContactID,
                                    label: item.FirstName + " " + item.LastName
                                })
                            }
                        }}
                        translate={translate}
                    />
                )}

                {currentTab.resource ===  Resources.WorkOrdersReportsMaintenanceCostTrailer && (
                    <WorkOrdersReportsTab
                        tableKey={'TrailerID'}
                        getFields={() => {
                            return {
                                TrailerID: new Field('TrailerID', '', [''] , false, 'select-search', {
                                    render: (item) => {
                                        if (!item) return null;

                                        if (item.TrailerID) {
                                            return (
                                                <CellButton
                                                    onClick={() => checkPerm(Resources.Trailer, READ_PERM) && handleViewTrailerClick(item.TrailerID)}
                                                >
                                                    {item.Trailer}
                                                </CellButton>
                                            )
                                        } else {
                                            return item.Trailer;
                                        }
                                    },
                                    addContainerClass: "col-span-full sm:col-span-6 lg:col-span-3"
                                }),
                                'PartsAmount': new Field('PartsAmount', '', [''], false, 'money', {
                                    addContainerClass: 'col-span-full sm:col-span-6 lg:col-span-3'
                                }),
                                'LaborAmount': new Field('LaborAmount', '', [''], false, 'money', {
                                    addContainerClass: 'col-span-full sm:col-span-6 lg:col-span-3'
                                }),
                                'OtherAmount': new Field('OtherAmount', '', [''], false, 'money', {
                                    addContainerClass: 'col-span-full sm:col-span-6 lg:col-span-3'
                                }),
                                'TotalAmount': new Field('TotalAmount', '', [''], false, 'money', {
                                    addContainerClass: 'col-span-full sm:col-span-6 lg:col-span-3'
                                }),
                            }
                        }}
                        additionalQuery={{
                            StartDate: new Field('StartDate', getSavedDates().StartDate || lastMonthStart(), [], false, 'date', {}, {}),
                            EndDate: new Field('EndDate', getSavedDates().EndDate || lastMonthEnd(), [], false, 'date', {}, {}),
                            TrailerIDs: new Field('TrailerIDs', '', [''], false, 'select-search', {}, {
                                multi: true,
                                all: true
                            }),
                            ChargeOrganizationIDs: new Field('ChargeOrganizationIDs', '', [''], false, 'select-search', {}, {
                                multi: true,
                                all: true
                            }),
                            ChargeContactIDs: new Field('ChargeContactIDs', '', [''], false, 'select-search', {}, {
                                multi: true,
                                all: true
                            }),
                            ScheduledMaintenanceIDs: new Field('ScheduledMaintenanceIDs', '', [''], false, 'select-search', {}, {
                                multi: true,
                                all: true
                            }),
                            UpdatedByContactID: new Field('UpdatedByContactID', '', [''], false, 'select-search', {}, {menuPlacement: "top", isClearable: true}),
                            CreatedByID: new Field('CreatedByID', '', [''], false, 'select-search', {}, {menuPlacement: "top", isClearable: true}),
                        }}
                        resourcePath={currentTab.resource}
                        storagePath={currentTab.name}
                        selects={{
                            TrailerIDs: {
                                api: 'api/' + Resources.TrailersQuick,
                                query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
                                searchMap: (item) => ({
                                    value: item.TrailerID,
                                    label: item.TrailerNumber
                                })
                            },
                            ChargeOrganizationIDs: {
                                api: 'api/' + Resources.OrganizationsQuick,
                                query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
                                searchMap: (item) => ({
                                    value: item.OrganizationID,
                                    label: item.LegalName
                                })
                            },
                            ChargeContactIDs: {
                                api: 'api/' + Resources.ContactsQuick,
                                query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
                                searchMap: (item) => ({
                                    value: item.ContactID,
                                    label: item.FirstName + " " + item.LastName
                                })
                            },
                            ScheduledMaintenanceIDs: {
                                api: 'api/' + Resources.WorkOrderScheduledMaintenance,
                                query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
                                searchMap: (item) => ({
                                    value: item.ScheduledMaintenanceID,
                                    label: item.ScheduledMaintenanceType + ", " + item.ScheduleNotes
                                })
                            },
                            UpdatedByContactID: {
                                api: 'api/' + Resources.ContactsQuick,
                                query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
                                searchMap: (item) => ({
                                    value: item.ContactID,
                                    label: item.FirstName + " " + item.LastName
                                })
                            },
                            CreatedByID: {
                                api: 'api/' + Resources.ContactsQuick,
                                query: DEFAULT_METADATA_SELECT_SEARCH_QUERY(),
                                searchMap: (item) => ({
                                    value: item.ContactID,
                                    label: item.FirstName + " " + item.LastName
                                })
                            }
                        }}
                        translate={translate}
                    />
                )}

                {currentTab.resource === Resources.WorkOrderProductServicesReport && (
                    <PartsReportTab
                        translate={translate}
                    />
                )}

                {currentTab.resource === Resources.StockTracking && (
                    <StockMovementReportTab
                        translate={translate}
                        pagePath={"stock-movement-report"}
                    />
                )}

                {currentTab.resource === Resources.ProfitMargin && (
                    <ProfitMarginReportTab
                        translate={translate}
                        pagePath={"profit-margin-report"}
                    />
                )}

                {currentTab.resource === Resources.WorkOrdersReportsMaintenanceCostMileage && (
                    <CostPerMileReportTab
                        translate={translate}
                    />
                )}

                {currentTab.resource === Resources.WorkOrdersReportsPerCode && (
                    <CostPerCodeReportTab
                        translate={translate}
                    />
                )}

                {currentTab.name === 'InventoryVelocity' && (
                    <InventoryVelocityReportTab
                        translate={translate}
                        // pagePath is the localStorage key for saved table options, and
                        // those options cache the resolved column labels. Anyone who
                        // opened this tab before the locale keys existed has the raw keys
                        // ("field.OverdueDays") frozen in their saved options -- a version
                        // bump only merges behaviour and style, never columns, because
                        // column labels can be renamed by the user and must not be
                        // clobbered. Changing the key is what retires those stale entries.
                        pagePath={"inventory-velocity-report-v2"}
                    />
                )}

                {currentTab.name === 'InventoryAging' && (
                    <InventoryAgingReportTab
                        translate={translate}
                        pagePath={"inventory-aging-report"}
                    />
                )}
            </Page>
        </Layout>
    )
}
